import React, { useCallback, useRef, useState } from 'react'
import { Badge, Drawer, IconButton, Popover, Tooltip } from '@mui/material'
import { Bell } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../router/routes'
import { useFormFactor } from '../../hooks'
import { useStyles } from './style'
import { NotificationPanel } from './notification-panel'
import { NotificationModals } from './modals'
import { useNotifications } from './hooks/useNotifications'
import { useNotificationAction } from './hooks/useNotificationAction'
import { useTrackNotificationEvent } from './hooks/useTrackNotificationEvent'

const ICON_SIZE = 24
const MAX_BADGE = 9

/**
 * Notification bell + panel. One component, mounted in all three toolbars
 * (enterprise, consumer, paid-consumer).
 *
 * Data comes from the `notifications` slice via `useNotifications`, which
 * follows the house fetch pattern (event-bus `onStart`/`onSuccess`/`onError`
 * driving `loading`/`error` in the slice). There is no backend yet, so the hook
 * serves mock data through that same path — see `NOTIFICATIONS_API_ENABLED`.
 * Whether the bell is mounted at all is the callers' call, via
 * `NOTIFICATIONS_ENABLED`; both flags live in ./constants.js.
 *
 * `id` is required by the enterprise/consumer toolbars: their `LinkBar` reads
 * `props.id` to derive the permission key (`notifications-action` -> `notifications`).
 *
 * `icon` lets each toolbar supply a glyph from its own set — the
 * enterprise/consumer bars are drawn with MUI Material icons, so a Phosphor bell
 * would sit oddly next to Skill Bites / Playlists / Me. Defaults to Phosphor
 * `Bell`, which is what the paid-consumer bar uses.
 */
export const NotificationBell = ({
  id = 'notifications-action',
  className,
  dataTest = 'notification-bell',
  icon
}) => {
  const classes = useStyles()
  const { isMobile } = useFormFactor()
  const navigate = useNavigate()
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)

  const {
    notifications,
    unreadCount,
    loading,
    error,
    load,
    markRead,
    markAllRead
  } = useNotifications()

  const {
    trackPanelOpened,
    trackPanelClosed,
    trackItemClicked,
    trackMarkAllRead,
    trackTabChanged,
    trackViewAllClicked,
    trackRetryClicked
  } = useTrackNotificationEvent()

  // Tracking is kept out of the setState updater — updaters must be pure, and
  // React invokes them twice under StrictMode, which would double-count.
  const handleToggle = useCallback(() => {
    const next = !open
    setOpen(next)
    if (next) trackPanelOpened(unreadCount)
    else trackPanelClosed()
  }, [open, unreadCount, trackPanelOpened, trackPanelClosed])

  const handleClose = useCallback(() => {
    if (open) trackPanelClosed()
    setOpen(false)
  }, [open, trackPanelClosed])

  const handleMarkAllRead = useCallback(() => {
    trackMarkAllRead(unreadCount)
    markAllRead()
  }, [markAllRead, trackMarkAllRead, unreadCount])

  const { activeModal, closeModal, runAction } = useNotificationAction()

  // Opening marks read (NC-21), then resolves the notification's action:
  // navigate, open the Mindset agent, or open a modal (NC-20; see
  // NOTIFICATION_ACTION in constants.js for which type gets which).
  const handleItemClick = useCallback(
    notification => {
      trackItemClicked(notification)
      markRead(notification)
      handleClose()
      runAction(notification.action)
    },
    [markRead, trackItemClicked, handleClose, runAction]
  )

  const handleRetry = useCallback(() => {
    trackRetryClicked()
    load()
  }, [load, trackRetryClicked])

  const handleViewAll = useCallback(() => {
    trackViewAllClicked()
    handleClose()
    navigate(routes.notifications.path)
  }, [trackViewAllClicked, handleClose, navigate])

  const label = unreadCount > 0
    ? `Notifications, ${unreadCount} unread`
    : 'Notifications'

  const panel = (
    <NotificationPanel
      notifications={notifications}
      loading={loading}
      error={error}
      onRetry={handleRetry}
      onMarkAllRead={handleMarkAllRead}
      onItemClick={handleItemClick}
      onTabChange={trackTabChanged}
      onViewAll={handleViewAll}
      showHandle={isMobile}
    />
  )

  return (
    <>
      <Tooltip title='Notifications'>
        <IconButton
          id={`${id}-button`}
          ref={anchorRef}
          onClick={handleToggle}
          className={`${classes.bellButton} ${className || ''}`}
          aria-label={label}
          aria-haspopup='dialog'
          aria-expanded={open}
          data-test={dataTest}
        >
          <Badge
            badgeContent={unreadCount}
            max={MAX_BADGE}
            color='primary'
            overlap='circular'
            className={classes.bellBadge}
            data-test='notification-badge'
          >
            {icon || <Bell size={ICON_SIZE} weight='regular' />}
          </Badge>
        </IconButton>
      </Tooltip>

      {isMobile
        ? (
          <Drawer
            anchor='bottom'
            open={open}
            onClose={handleClose}
            classes={{ paper: classes.drawerPaper }}
            data-test='notification-drawer'
          >
            {panel}
          </Drawer>
          )
        : (
          <Popover
            open={open}
            anchorEl={anchorRef.current}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            classes={{ paper: classes.popoverPaper }}
            disableScrollLock
            data-test='notification-popover'
          >
            {panel}
          </Popover>
          )}

      <NotificationModals activeModal={activeModal} onClose={closeModal} />
    </>
  )
}

export default NotificationBell
